import React, { useState } from 'react';
import { supabase } from '../../hooks/supabase';
import crossIcon from '../../assets/cross.svg';
import ErrorPopup from './errorPopup';
import createIcon from '../../assets/createWB.svg';

interface UpdatePasswordPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const UpdatePasswordPopup: React.FC<UpdatePasswordPopupProps> = ({ isOpen, onClose }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      setIsErrorPopupOpen(true);
      return;
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      onClose();
      // Navigate to signin with success message
      window.location.href = '/signin?message=Password updated successfully. Please sign in with your new password.';
    } catch (error) {
      console.error('Update password error:', error);
      setError(error instanceof Error ? error.message : 'Failed to update password');
      setIsErrorPopupOpen(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50">
      <div className="bg-[#FFFFFF] rounded-[16px] w-[808px] h-[274px] shadow-lg">
        {/* Header */}
        <div className="flex justify-between items-center h-[40px] px-6 py-4 border-b border-[#EEEFF1]">
          <div className="flex items-center gap-1">
            <img src={createIcon} alt="create" className="w-[16px] h-[16px] bg-[#266DF0] rounded-[4px] stroke-[#FFFFFF]" />
            <h2 className="text-[12px] font-normal text-[#222222]">
              Change password
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="text-[#75777C] hover:text-[#222222] transition-colors"
          >
            <img src={crossIcon} alt="close" className="w-2 h-2" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleUpdatePassword}>
          <div className="flex flex-col gap-4 px-[20px] py-[24px] text-left">
            <div>
              <label className="block text-sm font-normal text-[#5C5E63] mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordsMatch(e.target.value === confirmPassword);
                }}
                className="w-full h-[34px] px-4 rounded-[10px] border border-[#EEEFF1] text-[#222222] text-sm focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-normal text-[#5C5E63] mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setPasswordsMatch(e.target.value === newPassword);
                }}
                className={`w-full h-[34px] px-4 rounded-[10px] border text-[#222222] text-sm focus:outline-none focus:ring-1 focus:border-transparent ${
                  confirmPassword && !passwordsMatch 
                    ? 'border-[#FF0000] focus:ring-[#FF0000]' 
                    : 'border-[#EEEFF1] focus:ring-[#29DC5B]'
                }`}
                required
              />
              {confirmPassword && !passwordsMatch && (
                <p className="text-[#FF0000] text-xs mt-1">
                  Passwords do not match
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 h-[48px] px-[20px] py-2 border-t border-[#EEEFF1]">
            <button
              type="button"
              onClick={onClose}
              className="px-2 py-1 rounded-[8px] text-[#75777C] hover:bg-gray-100 transition-colors text-sm font-normal"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!newPassword || !confirmPassword || !passwordsMatch}
              className="px-2 rounded-[8px] bg-[#29DC5B] hover:bg-[#22c55e] text-[#000000] transition-colors text-sm font-normal disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Change password
            </button>
          </div>
        </form>
      </div>

      <ErrorPopup
        isOpen={isErrorPopupOpen}
        onClose={() => setIsErrorPopupOpen(false)}
        message={error || 'An error occurred'}
      />
    </div>
  );
};

export default UpdatePasswordPopup; 